import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * ImageLightbox — reusable fullscreen image viewer for classroom projection.
 *
 * Props
 * ─────
 *   open       : boolean
 *   onClose    : () => void
 *   src        : string  (image URL — required when open)
 *   alt        : string  (accessible alt text)
 *   caption    : string  (optional caption shown below the image)
 *   onPrev     : () => void   (optional — enables prev arrow)
 *   onNext     : () => void   (optional — enables next arrow)
 *   hasPrev    : boolean      (disables prev arrow at boundary)
 *   hasNext    : boolean      (disables next arrow at boundary)
 *   returnFocusRef : React.RefObject<HTMLElement>  (element to focus on close)
 *
 * Behaviour
 * ─────────
 *   • Escape         → close
 *   • ←/→ arrow keys → navigate (if onPrev/onNext supplied)
 *   • Click backdrop → close (image + controls don't propagate)
 *   • Body scroll locked while open
 *   • Focus trapped inside the dialog
 *   • Focus returned to `returnFocusRef` on close
 *   • Renders via portal into document.body
 *   • Respects `prefers-reduced-motion` (no fade animation)
 */
export default function ImageLightbox({
  open,
  onClose,
  src,
  alt = "",
  caption = "",
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  returnFocusRef = null,
}) {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Body scroll lock + focus initial control + focus return on close.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the close button after the portal mounts.
    const t = setTimeout(() => { closeBtnRef.current?.focus(); }, 0);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      // Return focus to the element that opened the lightbox.
      if (returnFocusRef && returnFocusRef.current) {
        try { returnFocusRef.current.focus(); } catch { /* noop */ }
      }
    };
  }, [open, returnFocusRef]);

  // Keyboard handling — Escape, arrows, and Tab (focus trap).
  const onKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose?.();
      return;
    }
    if (e.key === "ArrowLeft" && onPrev && hasPrev) {
      e.preventDefault();
      onPrev();
      return;
    }
    if (e.key === "ArrowRight" && onNext && hasNext) {
      e.preventDefault();
      onNext();
      return;
    }
    if (e.key === "Tab") {
      // Basic focus trap — cycle between the close button and the enabled
      // nav arrows. Since these are the only focusables inside the dialog,
      // we just prevent Tab from leaving them.
      const focusables = dialogRef.current?.querySelectorAll(
        "button:not([disabled])"
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  if (!open) return null;

  const stop = (e) => e.stopPropagation();

  const node = (
    <div
      ref={dialogRef}
      className="ftg-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={alt || "Enlarged image"}
      onClick={onClose}
      onKeyDown={onKeyDown}
      data-testid="image-lightbox"
    >
      <button
        ref={closeBtnRef}
        type="button"
        className="ftg-lightbox__close"
        aria-label="Close enlarged image"
        onClick={(e) => { stop(e); onClose?.(); }}
        data-testid="lightbox-close"
      >
        <X size={22} aria-hidden="true" />
      </button>

      {onPrev && (
        <button
          type="button"
          className="ftg-lightbox__nav ftg-lightbox__nav--prev"
          aria-label="Previous image"
          onClick={(e) => { stop(e); if (hasPrev) onPrev(); }}
          disabled={!hasPrev}
          data-testid="lightbox-prev"
        >
          <ChevronLeft size={26} aria-hidden="true" />
        </button>
      )}
      {onNext && (
        <button
          type="button"
          className="ftg-lightbox__nav ftg-lightbox__nav--next"
          aria-label="Next image"
          onClick={(e) => { stop(e); if (hasNext) onNext(); }}
          disabled={!hasNext}
          data-testid="lightbox-next"
        >
          <ChevronRight size={26} aria-hidden="true" />
        </button>
      )}

      <div className="ftg-lightbox__stage" onClick={stop}>
        <img
          src={src}
          alt={alt}
          className="ftg-lightbox__img"
          data-testid="lightbox-image"
          draggable={false}
        />
        {caption ? (
          <div className="ftg-lightbox__caption" data-testid="lightbox-caption">
            {caption}
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
